import { useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError } from '../api/client.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { useAuth } from '../state/auth.ts';
import { texts } from '../texts/de.ts';
import { AppFooter } from '../components/AppFooter.tsx';

/** Anmeldung und erste Einrichtung. */
export function AuthPage({ setupRequired }: { readonly setupRequired: boolean }) {
  const { setup, login } = useAuth();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (setupRequired && password !== repeat) {
      setError(texts.auth.passwordsDiffer);
      return;
    }

    setBusy(true);
    try {
      if (setupRequired) {
        await setup({ username, displayName, password });
      } else {
        await login({ username, password });
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-surface px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <AppIcon name="hat" size={40} className="text-accent" />
          <div>
            <h1 className="text-2xl font-bold">{texts.app.name}</h1>
            <p className="text-sm text-content-muted">{texts.app.tagline}</p>
          </div>
        </div>

        <form
          onSubmit={(event) => void submit(event)}
          className="rounded-card border border-border-subtle bg-surface-raised p-5 shadow-sm"
        >
          <h2 className="text-lg font-semibold">
            {setupRequired ? texts.auth.setupTitle : texts.auth.loginTitle}
          </h2>
          <p className="mt-1 mb-4 text-sm text-content-muted">
            {setupRequired ? texts.auth.setupIntro : texts.auth.loginIntro}
          </p>

          <label className="mb-3 block text-sm">
            <span className="mb-1 block font-medium">{texts.auth.username}</span>
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              required
              className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 outline-none focus:border-accent"
            />
          </label>

          {setupRequired ? (
            <label className="mb-3 block text-sm">
              <span className="mb-1 block font-medium">{texts.auth.displayName}</span>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
                className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 outline-none focus:border-accent"
              />
            </label>
          ) : null}

          <label className="mb-3 block text-sm">
            <span className="mb-1 block font-medium">{texts.auth.password}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={setupRequired ? 'new-password' : 'current-password'}
              required
              minLength={8}
              className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 outline-none focus:border-accent"
            />
          </label>

          {setupRequired ? (
            <label className="mb-3 block text-sm">
              <span className="mb-1 block font-medium">{texts.auth.passwordRepeat}</span>
              <input
                type="password"
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 outline-none focus:border-accent"
              />
            </label>
          ) : null}

          {error === null ? null : (
            <p role="alert" className="mb-3 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent disabled:opacity-60"
          >
            {busy ? texts.common.loading : setupRequired ? texts.auth.setup : texts.auth.login}
          </button>
        </form>
        <AppFooter className="mt-4" />
      </div>
    </div>
  );
}
